import { financialYearForDate } from "@/utils/helpers/financialYear"
import {
  addMonths,
  format,
  isBefore,
  parse,
  parseISO,
  startOfMonth,
  subYears,
} from "date-fns"
import { formatQueryDate } from "@/utils/helpers/formatters"

export type RebalanceFrequency = "Q" | "H" | "Y"

const REBALANCE_MONTHS: Record<RebalanceFrequency, number[]> = {
  Q: [2, 5, 8, 11], // Mar, Jun, Sep, Dec (0-indexed)
  H: [5, 11], // Jun, Dec
  Y: [5], // Jun
}

/**
 * Given a rebalance frequency and optional reference date, returns the next
 * rebalance date (always the 15th of the qualifying month).
 * Quarterly: 15 Mar/Jun/Sep/Dec
 * Half-yearly: 15 Jun/Dec
 * Annually: 15 Jun
 */
export function getNextRebalanceDate(
  frequency: RebalanceFrequency,
  from: Date = currentDate()
): Date {
  const months = REBALANCE_MONTHS[frequency]
  const year = from.getFullYear()
  const month = from.getMonth()
  const day = from.getDate()

  for (const targetMonth of months) {
    if (targetMonth > month || (targetMonth === month && day <= 15)) {
      return new Date(year, targetMonth, 15)
    }
  }

  // Wrap to the first rebalance month of the next year
  return new Date(year + 1, months[0], 15)
}

function getSearchParams() {
  // Check hash fragment first (for hash-based routing like #/investments?date=...)
  const hashMatch = window.location.hash.match(/\?([^#]+)/)
  if (hashMatch) {
    return new URLSearchParams(hashMatch[1])
  }
  // Fall back to regular query string
  return new URLSearchParams(window.location.search)
}

export function currentFinancialYear(date: Date = currentDate()) {
  return {
    ...financialYearForDate(date),
    isRolledBack: isRolledBack(),
  }
}

/**
 * Raw `date` query value (yyyyMMdd) for memo deps so cut-off month lists
 * recalculate when testers change ?date= without remounting the form.
 */
export function currentDateQueryParam(): string | null {
  return getSearchParams().get("date")
}

export function currentDate() {
  const mockDate = currentDateQueryParam()
  if (mockDate) {
    return parse(mockDate, "yMMd", new Date())
  }

  if (isRolledBack()) {
    return subYears(new Date(), 1)
  }

  return new Date()
}

export function isRolledBack() {
  return getSearchParams().has("rollback")
}

export function nextMonth() {
  const nextMonth = addMonths(startOfMonth(currentDate()), 1)
  return formatQueryDate(nextMonth)
}

export function isBeforeEOFY(date: Date) {
  const { endDate } = currentFinancialYear()
  return isBefore(date, endDate)
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

/**
 * Formats an ISO datetime string as "D Month" (e.g. "6 March").
 * Parses the date portion directly to avoid timezone conversion issues
 * (API uses +08:00 AWST).
 */
export function formatSeminarWebinarDate(isoStart: string): string {
  const datePart = isoStart.split("T")[0]
  const [, month, day] = datePart.split("-")
  const dayNum = parseInt(day, 10)
  const monthName = MONTH_NAMES[parseInt(month, 10) - 1]
  return `${dayNum} ${monthName}`
}

export function formatDayMonthYear(dateStr: string) {
  if (!dateStr) {
    return ""
  }

  const parsedCms = parse(dateStr, "dd/MM/yyyy", new Date())
  if (!isNaN(parsedCms.getTime())) {
    return format(parsedCms, "d MMMM yyyy")
  }

  const parsedIso = parseISO(dateStr)
  if (!isNaN(parsedIso.getTime())) {
    return format(parsedIso, "d MMMM yyyy")
  }

  const parsedFallback = new Date(dateStr)
  if (!isNaN(parsedFallback.getTime())) {
    return format(parsedFallback, "d MMMM yyyy")
  }

  return dateStr
}
